import styles from './CalendarDropdown.module.css';
import React, { useState } from 'react';

export const CalendarDropdown = ({ 
  onDateSelect, 
  selectedDate,
  onClose 
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('month'); // 'month' or 'year'
  const [tempSelectedDate, setTempSelectedDate] = useState(null);

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  // Check if a date is in the past
  const isDateInPast = (date) => {
    const now = new Date();
    const selectedDateOnly = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    );
    const todayOnly = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    );
    return selectedDateOnly < todayOnly;
  };

  // Check if a date is today
  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  const handleDateClick = (day) => {
    const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    
    // Validate if selected date is in the past
    if (isDateInPast(newDate)) {
      alert('Cannot select past dates. Please select a future date.');
      return;
    }
    
    setTempSelectedDate(newDate);
  };

  const handleMonthClick = (month) => {
    setCurrentDate(new Date(currentDate.getFullYear(), month, 1));
    setView('month');
  };

  const handleYearClick = (year) => {
    setCurrentDate(new Date(year, currentDate.getMonth(), 1));
    setView('month');
  };

  const formatDate = (date) => {
    console.log('formatDate received:', date); // Debug log
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear().toString().slice(-2);
    
    const result = `${day}/${month}/${year}`;
    console.log('formatDate returning:', result); // Debug log
    return result;
  };

  const handleOk = () => {
    if (tempSelectedDate) {
      const dateString = formatDate(tempSelectedDate);
      onDateSelect(dateString);
    }
    onClose();
  };

  // FIXED: Proper month navigation
  const navigateMonth = (direction) => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + direction, 1));
  };

  // FIXED: Proper year navigation
  const navigateYear = (direction) => {
    setCurrentDate(new Date(currentDate.getFullYear() + direction, currentDate.getMonth(), 1));
  };

  const toggleView = () => {
    setView(view === 'month' ? 'year' : 'month');
  };

  // Check if a day should be disabled (past date)
  const isDayDisabled = (day) => {
    const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    const today = new Date();
    
    // Reset hours for date comparison only
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    
    return dateOnly < todayOnly;
  };

  // Generate month view
  const renderMonthView = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className={styles.calendarEmptyCell}></div>);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const isSelected = tempSelectedDate && 
                        tempSelectedDate.getDate() === day && 
                        tempSelectedDate.getMonth() === currentDate.getMonth() && 
                        tempSelectedDate.getFullYear() === currentDate.getFullYear();
      const isToday = new Date().getDate() === day && 
                     new Date().getMonth() === currentDate.getMonth() && 
                     new Date().getFullYear() === currentDate.getFullYear();
      const isDisabled = isDayDisabled(day);
      
      days.push(
        <div
          key={day}
          className={`${styles.calendarDay} 
            ${isSelected ? styles.calendarDaySelected : ''} 
            ${isToday ? styles.calendarDayToday : ''}
            ${isDisabled ? styles.calendarDayDisabled : ''}`}
          onClick={() => !isDisabled && handleDateClick(day)}
        >
          {day}
          {isDisabled && <div className={styles.disabledOverlay}></div>}
        </div>
      );
    }

    return (
      <>
        <div className={styles.calendarWeekdays}>
          <div className={styles.calendarWeekday}>S</div>
          <div className={styles.calendarWeekday}>M</div>
          <div className={styles.calendarWeekday}>T</div>
          <div className={styles.calendarWeekday}>W</div>
          <div className={styles.calendarWeekday}>T</div>
          <div className={styles.calendarWeekday}>F</div>
          <div className={styles.calendarWeekday}>S</div>
        </div>
        
        <div className={styles.calendarGrid}>
          {days}
        </div>
      </>
    );
  };

  // Generate year view
  const renderYearView = () => {
    const currentYear = currentDate.getFullYear();
    const years = [];
    
    // Show 12 years: current year -5 to current year +6
    for (let year = currentYear - 5; year <= currentYear + 6; year++) {
      const isCurrentYear = year === new Date().getFullYear();
      const isPastYear = year < new Date().getFullYear();
      years.push(
        <div
          key={year}
          className={`${styles.yearItem} 
            ${isCurrentYear ? styles.yearItemCurrent : ''}
            ${isPastYear ? styles.yearItemDisabled : ''}`}
          onClick={() => !isPastYear && handleYearClick(year)}
        >
          {year}
        </div>
      );
    }

    return (
      <div className={styles.yearGrid}>
        {years}
      </div>
    );
  };

  return (
    <div className={styles.calendarDropdown}>
      <div className={styles.calendertitle}> 
        <span>Select your offer expiry date</span>
      </div>
      
      <div className={styles.calendarHeader}>
        <button 
          type="button" 
          onClick={() => view === 'month' ? navigateMonth(-1) : navigateYear(-1)}
          className={styles.calendarNavButton}
        >
          ‹
        </button>
        
        <button 
          type="button" 
          onClick={toggleView}
          className={styles.calendarTitle}
        >
          {view === 'month' 
            ? `${currentDate.toLocaleString('default', { month: 'long' })} ${currentDate.getFullYear()}`
            : `Select Year`
          }
        </button>
        
        <button 
          type="button" 
          onClick={() => view === 'month' ? navigateMonth(1) : navigateYear(1)}
          className={styles.calendarNavButton}
        >
          ›
        </button>
      </div>
      
      {view === 'month' ? renderMonthView() : renderYearView()}
      
      <div className={styles.validationMessage}>
        {tempSelectedDate && isDateInPast(tempSelectedDate) && (
          <span className={styles.errorText}>Selected date is in the past</span>
        )}
      </div>
      
      <div className={styles.calendarActions}>
        <button 
          type="button" 
          onClick={onClose}
          className={styles.calendarCloseButton}
        >
          Cancel
        </button>
        <button 
          type="button" 
          onClick={handleOk}
          className={styles.calendarOkButton}
          disabled={!tempSelectedDate || isDateInPast(tempSelectedDate)}
        >
          OK
        </button>
      </div>
    </div>
  );
};